import React, { useState, useMemo, useEffect } from 'react';
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaFilter,
  FaDownload,
  FaSearch,
  FaUser,
  FaStore,
  FaTools,
  FaTag,
  FaBan,
  FaCheckCircle,
  FaTimesCircle,
  FaGift,
  FaShieldAlt,
  FaStar,
  FaSpinner,
  FaPhone,
  FaEnvelope,
  FaArrowLeft,
  FaSave,
  FaUserCircle
} from 'react-icons/fa';
import { userService } from '../../../../services/apiService';
import AdminLocationDropdown from '../components/AdminLocationDropdown';
import { getProfileDisplayEmail, getProfileDisplayName } from '../../../../utils/displayName';
import { toast } from 'react-toastify';

const Users = () => {
  const [view, setView] = useState('list'); // 'list', 'edit', 'view', 'discount'
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(() => {
    try {
      const raw = localStorage.getItem('adminLocation');
      if (!raw) return '';
      try {
        return JSON.parse(raw);
      } catch (e) {
        return raw;
      }
    } catch (e) {
      return '';
    }
  });

  const [formData, setFormData] = useState({
    name: '', email: '', role: 'customer', status: 'Active'
  });
  const [selectedUserOrderCount, setSelectedUserOrderCount] = useState(null);

  // Fetch live users data from API
  useEffect(() => {
    fetchUsers();
  }, [selectedLocation]);

  // Fetch order count when viewing a user
  useEffect(() => {
    if (view !== 'view' || !selectedUser?.id) {
      setSelectedUserOrderCount(null);
      return;
    }
    let cancelled = false;
    userService.getOrderCount(selectedUser.id)
      .then(({ count }) => { if (!cancelled) setSelectedUserOrderCount(count); })
      .catch(() => { if (!cancelled) setSelectedUserOrderCount(0); });
    return () => { cancelled = true; };
  }, [view, selectedUser?.id]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Build filters depending on selectedLocation shape
      let filters = {};
      if (selectedLocation) {
        if (typeof selectedLocation === 'object') {
          // object: { city, state, country, area? }
          filters = { ...(selectedLocation.city ? { city: selectedLocation.city } : {}), ...(selectedLocation.state ? { state: selectedLocation.state } : {}), ...(selectedLocation.country ? { country: selectedLocation.country } : {}), ...(selectedLocation.area ? { area: selectedLocation.area } : {}) };
        } else {
          filters = { location: selectedLocation };
        }
      }
      const response = await userService.getAllUsers(Object.keys(filters).length ? filters : {});
      // Handle different response structures
      const users = response.data || response.users || response;
      // Transform users data to match the component's expected format
      let transformedUsers = Array.isArray(users) ? users.map(user => ({
        id: user._id || user.id,
        name: user.name || '—',
        email: (user.email && String(user.email).trim()) ? user.email : '—',
        role: user.role || 'customer',
        status: user.isActive ? 'Active' : 'Inactive',
        joined: user.createdAt ? new Date(user.createdAt).toISOString().split('T')[0] : 'N/A',
        orders: 0,
        rating: null,
        revenue: null,
        lastLogin: null,
        phone: user.phone || '',
        phoneCode: user.phoneCode || '+91',
        customerDetails: user.customerDetails || null,
        vendorDetails: user.vendorDetails || null,
        mechanicDetails: user.mechanicDetails || null,
        garageDetails: user.garageDetails || null,
        shippingDetails: user.shippingDetails || null
      })) : [];
      setUsersData(transformedUsers);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (type, user) => {
    setSelectedUser(user);
    if (type === 'edit') {
      setFormData({ 
        ...user,
        role: user.role || 'customer',
        status: user.status || 'Active'
      });
      setView('edit');
    } else if (type === 'view') {
      setView('view');
    } else if (type === 'discount') {
      setView('discount');
    } else if (type === 'delete') {
      const displayName = user.vendorDetails?.storeName || user.vendorDetails?.businessName || user.mechanicDetails?.name || user.garageDetails?.garageName || user.shippingDetails?.name || getProfileDisplayName(user) || '—';
      if (window.confirm(`Delete ${displayName}?`)) {
        handleDeleteUser(user.id);
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Convert status back to isActive
      const updateData = {
        name: (formData.name && formData.name !== '—' ? formData.name : '').trim() || undefined,
        email: (formData.email && formData.email !== '—' ? formData.email : '').trim() || undefined,
        role: formData.role,
        isActive: formData.status === 'Active'
      };
      
      await userService.updateUser(selectedUser.id, updateData);
      toast.success(`User "${formData.name}" updated successfully!`);
      setView('list');
      await fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setLoading(true);
      await userService.deleteUser(userId);
      toast.success('User deleted successfully!');
      await fetchUsers(); // Refresh users list
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    toast.info('Filters cleared');
  };

  // Export users data to CSV
  const handleExport = () => {
    try {
      // Use filtered users for export
      const dataToExport = filteredUsers.length > 0 ? filteredUsers : usersData;
      
      // Generate CSV content
      let csvContent = 'User Management Report\n';
      csvContent += `Generated: ${new Date().toLocaleString()}\n`;
      csvContent += `Total Users: ${dataToExport.length}\n\n`;
      
      // CSV Headers
      csvContent += 'Name,Email,Account Type,Status,Orders/Jobs,Rating,Revenue,Last Login,Phone,Joined Date\n';
      
      // CSV Data
      dataToExport.forEach(user => {
        const name = (user.name || '').replace(/,/g, ';');
        const email = (user.email || '').replace(/,/g, ';');
        const role = formatRoleName(user.role || 'customer');
        const status = (user.status || 'N/A').replace(/,/g, ';');
        const orders = user.orders || 0;
        const rating = user.rating || 'N/A';
        const revenue = user.revenue || 'N/A';
        const lastLogin = user.lastLogin || 'Never';
        const phone = (user.phone || '').replace(/,/g, ';');
        const joined = user.joined || 'N/A';
        
        csvContent += `${name},${email},${role},${status},${orders},${rating},${revenue},${lastLogin},${phone},${joined}\n`;
      });
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${dataToExport.length} users successfully!`);
    } catch (error) {
      console.error('Failed to export users:', error);
      toast.error('Failed to export users: ' + error.message);
    }
  };

  // Use live users data (no mock data)
  const filteredUsers = useMemo(() => {
    return usersData.filter(user => {
      const matchesSearch =
        (user.name || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (user.email || '').toLowerCase().includes((searchTerm || '').toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || (user.status || '').toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [usersData, searchTerm, roleFilter, statusFilter]);

  const getRoleIcon = (role) => {
    switch (role) {
      case 'vendor': return <FaStore className="text-green-600" />;
      case 'mechanics': return <FaTools className="text-blue-600" />;
      case 'superadmin':
      case 'admin': return <FaShieldAlt className="text-red-600" />;
      case 'garage': return <FaTools className="text-orange-600" />;
      case 'shipping': return <FaStore className="text-indigo-600" />;
      default: return <FaUser className="text-gray-600" />;
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      vendor: 'bg-green-100 text-green-800',
      mechanics: 'bg-blue-100 text-blue-800',
      customer: 'bg-purple-100 text-purple-800',
      superadmin: 'bg-red-100 text-red-800',
      admin: 'bg-red-100 text-red-800',
      garage: 'bg-orange-100 text-orange-800',
      shipping: 'bg-indigo-100 text-indigo-800'
    };
    return badges[role] || 'bg-gray-100 text-gray-800';
  };

  // Format role name for display
  const formatRoleName = (role) => {
    const roleMap = {
      'customer': 'Customer',
      'vendor': 'Vendor',
      'mechanics': 'Mechanics',
      'superadmin': 'Super Admin',
      'admin': 'Admin',
      'garage': 'Garage',
      'shipping': 'Shipping'
    };
    return roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  const formatDetailValue = (v) => {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (Array.isArray(v) && v.length > 0 && typeof v[0] !== 'object') return v.join(', ');
    if (typeof v === 'object' && v !== null) return null;
    return v == null ? '' : String(v);
  };

  const renderDetails = (obj, sectionTitle = null) => {
    if (!obj || typeof obj !== 'object') return null;
    const entries = Object.entries(obj).filter(([k]) => k !== 'password' && k !== 'passwordHash');
    const hasNested = entries.some(([, v]) => typeof v === 'object' && v !== null && (Array.isArray(v) ? v.length > 0 && typeof v[0] === 'object' : Object.keys(v).length > 0));
    if (entries.length === 0 && !hasNested) return null;
    return (
      <div className="space-y-4">
        {sectionTitle && <h5 className="text-xs font-black text-gray-500 uppercase tracking-widest">{sectionTitle}</h5>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {entries.map(([k, v]) => {
            const label = String(k).replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
            if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
              return (
                <div key={k} className="sm:col-span-2 lg:col-span-3 space-y-3">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{label}</p>
                  {v.map((item, idx) => (
                    <div key={idx} className="p-4 bg-gray-100 rounded-xl border border-gray-200 space-y-2">
                      <p className="text-[10px] font-black text-gray-500 uppercase">{label} {v.length > 1 ? idx + 1 : ''}</p>
                      {renderDetails(item)}
                    </div>
                  ))}
                </div>
              );
            }
            if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 0) {
              return (
                <div key={k} className="sm:col-span-2 lg:col-span-3">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{label}</p>
                  <div className="p-4 bg-gray-100 rounded-xl border border-gray-200">{renderDetails(v)}</div>
                </div>
              );
            }
            const display = formatDetailValue(v);
            return (
              <div key={k} className="p-4 bg-gray-50 rounded-2xl">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
                <p className="font-bold text-gray-900 break-words mt-1">{display == null ? '' : display}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getRoleDetails = (user) => {
    const r = (user?.role || '').toLowerCase();
    if (r === 'vendor' && user?.vendorDetails) return { label: 'Vendor Details', data: user.vendorDetails };
    if (r === 'mechanics' && user?.mechanicDetails) return { label: 'Mechanic Details', data: user.mechanicDetails };
    if (r === 'garage' && user?.garageDetails) return { label: 'Garage Details', data: user.garageDetails };
    if (r === 'shipping' && user?.shippingDetails) return { label: 'Delivery / Shipping Details', data: user.shippingDetails };
    if (r === 'customer' && user?.customerDetails) return { label: 'Customer Details', data: user.customerDetails };
    return null;
  };

  const getProfileImage = (u) => (u?.logo || u?.avatar || u?.profilePhoto || u?.image || u?.vendorDetails?.logo || u?.vendorDetails?.storeLogo || u?.garageDetails?.logo || u?.mechanicDetails?.logo || '');

  if (view === 'view' && selectedUser) {
    const roleDetails = getRoleDetails(selectedUser);
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-gray-900 uppercase">User Profile</h2>
          <button onClick={() => setView('list')} className="text-gray-500 font-bold hover:text-gray-900">← Back</button>
        </div>
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 flex flex-col md:flex-row gap-8">
          <div className="w-24 h-24 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-lg overflow-hidden bg-blue-600 font-black text-3xl">
            {getProfileImage(selectedUser) ? (
              <img loading="lazy" src={getProfileImage(selectedUser)} alt="" className="w-full h-full object-cover" />
            ) : (
              ((getProfileDisplayName(selectedUser) || selectedUser.vendorDetails?.storeName || selectedUser.mechanicDetails?.name || selectedUser.garageDetails?.garageName || selectedUser.shippingDetails?.name || '').trim() || '—').charAt(0)
            )}
          </div>
          <div className="flex-1 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-3xl font-black text-gray-900">{selectedUser.vendorDetails?.storeName || selectedUser.vendorDetails?.businessName || selectedUser.mechanicDetails?.name || selectedUser.garageDetails?.garageName || selectedUser.shippingDetails?.name || getProfileDisplayName(selectedUser) || '—'}</h3>
                <p className="text-gray-500 font-bold">{getProfileDisplayEmail(selectedUser) || '—'}</p>
                {(selectedUser.phone || selectedUser.phoneCode) && (
                  <p className="text-gray-500 font-bold mt-1">{(selectedUser.phoneCode || '') + ' ' + (selectedUser.phone || '')}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleAction('edit', selectedUser)} className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition shadow-sm">Edit profile</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-2xl">
                <p className="text-[10px] font-black text-gray-400 uppercase">Account Type</p>
                <p className="font-bold text-gray-900">{selectedUser.role ? formatRoleName(selectedUser.role) : ''}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl">
                <p className="text-[10px] font-black text-gray-400 uppercase">Status</p>
                <p className="font-bold text-gray-900 uppercase">{selectedUser.status ?? ''}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl">
                <p className="text-[10px] font-black text-gray-400 uppercase">Total Orders</p>
                <p className="font-bold text-gray-900">{selectedUserOrderCount !== null ? selectedUserOrderCount : (selectedUser.orders ?? '—')}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl">
                <p className="text-[10px] font-black text-gray-400 uppercase">Registered</p>
                <p className="font-bold text-gray-900">{selectedUser.joined ?? selectedUser.registrationDate ?? ''}</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-black text-gray-700 uppercase tracking-widest mb-4">All data</h4>
              <p className="text-xs text-gray-500 mb-3">Complete profile and account details</p>
              {renderDetails(selectedUser)}
            </div>
            {roleDetails && roleDetails.data && Object.keys(roleDetails.data).length > 0 && (
              <div>
                <h4 className="text-sm font-black text-gray-700 uppercase tracking-widest mb-4">{roleDetails.label}</h4>
                <p className="text-xs text-gray-500 mb-3">All details filled during onboarding</p>
                {renderDetails(roleDetails.data)}
              </div>
            )}
            {selectedUser.privileges && (
              <div className="p-6 bg-blue-50 rounded-2xl border border-blue-100">
                <h4 className="text-[10px] font-black text-blue-900 uppercase tracking-widest mb-4">Account Privileges</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.privileges.map(p => <span key={p} className="px-3 py-1 bg-white rounded-full text-xs font-bold text-blue-600 border border-blue-100 shadow-sm">{p}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'edit' && selectedUser) {
    const editDisplayName =
      selectedUser.vendorDetails?.storeName ||
      selectedUser.vendorDetails?.businessName ||
      selectedUser.mechanicDetails?.name ||
      selectedUser.garageDetails?.garageName ||
      selectedUser.shippingDetails?.name ||
      formData.name ||
      getProfileDisplayName(selectedUser) ||
      'User';
    const editEmail =
      formData.email && formData.email !== '—' ? formData.email : '';
    const editPhone = [formData.phoneCode || selectedUser.phoneCode, formData.phone || selectedUser.phone]
      .filter(Boolean)
      .join(' ')
      .trim();

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setView('list')}
              className="p-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              aria-label="Back to user list"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h2 className="text-2xl font-black text-gray-900">Modify User</h2>
              <p className="text-sm text-gray-500 mt-0.5">Update account details, role, and status</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
            ID: {String(selectedUser.id).slice(-8)}
          </span>
        </div>

        {/* Profile summary */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-3xl font-black text-white shrink-0 shadow-md overflow-hidden">
                {getProfileImage(selectedUser) ? (
                  <img loading="lazy" src={getProfileImage(selectedUser)} alt="" className="w-full h-full object-cover" />
                ) : (
                  editDisplayName.charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{editDisplayName}</h3>
                <p className="text-gray-600 text-sm mt-1 truncate flex items-center gap-2">
                  <FaEnvelope className="text-gray-400 shrink-0 text-xs" />
                  {editEmail || 'No email on file'}
                </p>
                {editPhone && (
                  <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                    <FaPhone className="text-gray-400 shrink-0 text-xs" />
                    {editPhone}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${getRoleBadge(formData.role)}`}>
                    {getRoleIcon(formData.role)}
                    {formatRoleName(formData.role)}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      formData.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : formData.status === 'Suspended'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {formData.status}
                  </span>
                  {selectedUser.joined && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      Joined {selectedUser.joined}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 sm:px-8 py-5 border-b border-gray-100 bg-gray-50/80">
            <h4 className="text-sm font-black text-gray-800 uppercase tracking-wider flex items-center gap-2">
              <FaUserCircle className="text-blue-600" />
              Account information
            </h4>
            <p className="text-xs text-gray-500 mt-1">Changes apply immediately after you save</p>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="edit-user-name" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Full name
                </label>
                <div className="relative">
                  <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    id="edit-user-name"
                    required
                    type="text"
                    className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={formData.name === '—' ? '' : formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-user-email" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Email address
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    id="edit-user-email"
                    type="email"
                    className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={editEmail}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <p className="text-[11px] text-gray-400">Leave empty for phone-only accounts</p>
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-user-phone" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Phone number
                </label>
                <div className="relative">
                  <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    id="edit-user-phone"
                    type="tel"
                    readOnly
                    className="w-full pl-11 pr-4 py-3.5 bg-gray-100 border border-gray-200 rounded-xl font-semibold text-gray-600 cursor-not-allowed"
                    value={editPhone || 'Not provided'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-user-joined" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Registered on
                </label>
                <input
                  id="edit-user-joined"
                  type="text"
                  readOnly
                  className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-xl font-semibold text-gray-600 cursor-not-allowed"
                  value={selectedUser.joined || 'N/A'}
                />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-8">
              <h4 className="text-sm font-black text-gray-800 uppercase tracking-wider flex items-center gap-2 mb-6">
                <FaShieldAlt className="text-indigo-600" />
                Role &amp; access
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="edit-user-role" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Account type
                  </label>
                  <select
                    id="edit-user-role"
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition appearance-none cursor-pointer"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                    <option value="mechanics">Mechanics</option>
                    <option value="garage">Garage</option>
                    <option value="shipping">Shipping</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="edit-user-status" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Account status
                  </label>
                  <select
                    id="edit-user-status"
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition appearance-none cursor-pointer"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
              </div>

              {formData.status !== 'Active' && (
                <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <FaBan className="text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">
                    Inactive or suspended users cannot sign in until status is set back to Active.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="px-6 sm:px-8 py-5 border-t border-gray-100 bg-gray-50/50 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => setView('list')}
              disabled={loading}
              className="px-6 py-3.5 rounded-xl border border-gray-300 font-bold text-gray-700 hover:bg-white transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <FaSave />
                  Save changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">User Management</h2>
          <p className="text-xs text-gray-600 mt-1">Manage customers, vendors, and mechanics</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleClearFilters}
            disabled={searchTerm === '' && roleFilter === 'all' && statusFilter === 'all'}
            className="btn-outline flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"
            title="Clear all filters"
          >
            <FaTimesCircle /> Clear Filters
          </button>
          <button 
            onClick={handleExport}
            disabled={loading || (filteredUsers.length === 0 && usersData.length === 0)}
            className="bg-white border border-red-500 text-red-500 rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors"
            title="Export users to CSV"
          >
            {loading ? <FaSpinner className="animate-spin text-red-500" /> : <FaDownload className="text-red-500" />} <span className="text-red-500">Export</span>
          </button>
          <div className="hidden md:flex items-center">
            <AdminLocationDropdown
              value={selectedLocation}
              onChange={(v) => {
                setSelectedLocation(v);
                try {
                  if (!v) {
                    localStorage.removeItem('adminLocation');
                  } else if (typeof v === 'object') {
                    localStorage.setItem('adminLocation', JSON.stringify(v));
                  } else {
                    localStorage.setItem('adminLocation', String(v));
                  }
                } catch (e) {}
              }}
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Account Types</option>
            <option value="customer">Customers</option>
            <option value="vendor">Vendors</option>
            <option value="mechanics">Mechanics</option>
            <option value="garage">Garages</option>
            <option value="shipping">Shipping</option>
            <option value="superadmin">Super Admins</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>
      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-md border border-gray-100 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      )}

      {/* Users Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders/Jobs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const displayName = user.vendorDetails?.storeName || user.vendorDetails?.businessName || user.mechanicDetails?.name || user.garageDetails?.garageName || user.shippingDetails?.name || getProfileDisplayName(user) || '—';
                const displayEmail = getProfileDisplayEmail(user) || '—';
                return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                        {(displayName !== '—' ? displayName : '').charAt(0).toUpperCase() || '—'}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{displayName}</div>
                        <div className="text-xs text-gray-500">{displayEmail}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getRoleIcon(user.role)}
                      <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadge(user.role)}`}>
                        {formatRoleName(user.role)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${user.status === 'Active' ? 'bg-green-100 text-green-800' :
                        user.status === 'Suspended' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{user.orders || 0}</td>
                  <td className="px-6 py-4">
                    {user.rating ? (
                      <div className="flex items-center gap-1">
                        <FaStar className="text-yellow-500 text-xs" />
                        <span className="text-sm font-semibold">{user.rating}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{user.revenue || 'N/A'}</td>
                  <td className="px-6 py-4 text-xs text-gray-500">{user.lastLogin || 'Never'}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleAction('view', user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleAction('edit', user)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleAction('delete', user)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Empty State */}
      {!loading && filteredUsers.length === 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-100 p-8 text-center">
          <p className="text-gray-600">No users found matching your filters.</p>
        </div>
      )}

    </div>
  );
};

export default Users;

